'use client';

import { VideoHTMLAttributes, useEffect, useRef } from 'react';

import clsx from 'clsx';

import styles from './video.module.scss';

/** Minimal valid WebVTT when no caption file is supplied (media element track requirement). */
const PLACEHOLDER_CAPTIONS_VTT =
  'data:text/vtt;charset=utf-8,' + encodeURIComponent('WEBVTT\n\n');

export interface VideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  noFullscreen?: boolean;
  noDownload?: boolean;
  dynamicSrcLoad?: boolean;
  preLoad?: boolean;
  createdAt: string;
  thumbnailUrl?: string;
  description?: string;
  title?: string;
  src: string;
  posterUrl?: string;
  type?: string;
  /** Optional VTT URL for captions; placeholder data URL is used when omitted. */
  captionsSrc?: string;
}

export const Video = ({
  preLoad,
  noFullscreen = false,
  noDownload = false,
  disableRemotePlayback = false,
  dynamicSrcLoad = false,
  className = '',
  createdAt,
  children,
  title,
  description = '',
  thumbnailUrl,
  src,
  type = 'video/mp4',
  captionsSrc,
  ...props
}: VideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (dynamicSrcLoad && videoRef.current && src) {
      const videoElement = videoRef.current;

      if (videoElement.currentSrc !== src) {
        videoElement.src = src;
        videoElement.load();
      }
    }
  }, [src, dynamicSrcLoad]);

  return (
    <div itemScope itemType='https://schema.org/VideoObject'>
      <video
        data-testid='video'
        {...props}
        ref={videoRef}
        preload={preLoad ? 'auto' : 'none'}
        controlsList={getEnabledControls({
          noDownload,
          noFullscreen,
          disableRemotePlayback,
        })}
        className={clsx(styles['video'], className)}
      >
        <track
          kind='captions'
          srcLang='en'
          label='Captions'
          src={captionsSrc ?? PLACEHOLDER_CAPTIONS_VTT}
        />
        {/* Render sources passed from parent */}
        {children}

        {/* Otherwise render standard src */}
        {!dynamicSrcLoad && <source src={src} type={type} />}
      </video>

      <meta itemProp='name' content={title} />
      <meta itemProp='description' content={description} />
      <meta itemProp='uploadDate' content={createdAt} />
      <meta itemProp='contentUrl' content={src} />
      <meta itemProp='thumbnailUrl' content={thumbnailUrl} />
    </div>
  );
};

const getEnabledControls = (
  controlsObj: Record<string, boolean>
): string | undefined =>
  Object.entries(controlsObj)
    .filter(([_, value]) => Boolean(value))
    .map(([key]) => key.toLowerCase())
    .join(' ') || undefined;
